// The maths behind the hand-tracking slides, kept free of React and the
// camera so it can be tested on its own (node --test client/src/lib/*.test.js).
// Landmarks are MediaPipe Hand Landmarker's: 21 points, 0 = wrist,
// 4 = thumb tip, 8 = index tip. Image landmarks are 0-1 across the frame;
// world landmarks are metres, centred on the hand.

export function dist(p1, p2) {
  const dx = p1.x - p2.x;
  const dy = p1.y - p2.y;
  return Math.sqrt(dx * dx + dy * dy);
}

export function analyzeFingers(landmarks) {
  if (!landmarks || landmarks.length < 21) {
    return { count: 0, extended: [false, false, false, false, false], gesture: null };
  }

  const wrist = landmarks[0];
  const pinkyMcp = landmarks[17];

  // Thumb
  const thumbTip = landmarks[4];
  const thumbIp = landmarks[3];
  const thumbMcp = landmarks[2];
  const thumbExtended =
    dist(thumbTip, pinkyMcp) > dist(thumbIp, pinkyMcp) * 1.15 &&
    dist(thumbTip, wrist) > dist(thumbMcp, wrist) * 1.1;

  // 4 fingers
  const indexExtended =
    dist(landmarks[8], wrist) > dist(landmarks[6], wrist) * 1.12 &&
    landmarks[8].y < landmarks[6].y + 0.05;

  const middleExtended =
    dist(landmarks[12], wrist) > dist(landmarks[10], wrist) * 1.12 &&
    landmarks[12].y < landmarks[10].y + 0.05;

  const ringExtended =
    dist(landmarks[16], wrist) > dist(landmarks[14], wrist) * 1.12 &&
    landmarks[16].y < landmarks[14].y + 0.05;

  const pinkyExtended =
    dist(landmarks[20], wrist) > dist(landmarks[18], wrist) * 1.12 &&
    landmarks[20].y < landmarks[18].y + 0.05;

  const extended = [thumbExtended, indexExtended, middleExtended, ringExtended, pinkyExtended];
  const count = extended.filter(Boolean).length;

  // Gestures (No emojis)
  let gesture = null;
  const [t, i, m, r, p] = extended;

  if (count === 0) {
    gesture = { name: 'กำมือ' };
  } else if (count === 1) {
    if (t && !i && !m && !r && !p) {
      gesture = { name: 'เยี่ยมยอด' };
    } else {
      gesture = { name: '1 นิ้ว' };
    }
  } else if (count === 2) {
    if (i && m && !r && !p) {
      gesture = { name: '2 นิ้ว (สู้ๆ)' };
    } else {
      gesture = { name: '2 นิ้ว' };
    }
  } else if (count === 3) {
    if (t && i && p && !m && !r) {
      gesture = { name: 'I Love You' };
    } else {
      gesture = { name: '3 นิ้ว' };
    }
  } else if (count === 4) {
    gesture = { name: '4 นิ้ว' };
  } else if (count === 5) {
    gesture = { name: '5 นิ้ว (ไฮไฟฟ์)' };
  }

  // Mini heart check
  if (dist(thumbTip, landmarks[8]) < 0.06 && !m && !r && !p) {
    gesture = { name: 'มินิฮาร์ท' };
  }

  return { count, extended, gesture };
}

/** Thumb tip to index tip, in centimetres, from world landmarks. */
export function pinchCm(worldLandmarks) {
  const a = worldLandmarks[4];
  const b = worldLandmarks[8];
  return Math.hypot(a.x - b.x, a.y - b.y, (a.z || 0) - (b.z || 0)) * 100;
}

/**
 * A number as Python would print it: an int as digits, a float rounded to
 * two places and always with its decimal point — `5.0`, never `5`, because
 * telling those apart is the lesson.
 */
export function pyRepr(value, kind) {
  if (kind === 'int') return String(Math.round(value));
  const r = Math.round(value * 100) / 100;
  return Number.isInteger(r) ? r.toFixed(1) : String(r);
}

/**
 * Which detected hand wears the `left` tag and which the `right`. The video
 * is shown mirrored, so a hand's place on screen is 1 - its image x. Two
 * hands split by screen position; a lone hand takes the half it is in.
 */
export function assignHands(hands) {
  const placed = (hands || []).map((hand) => ({ hand, x: 1 - hand.landmarks[0].x }));
  if (placed.length >= 2) {
    placed.sort((a, b) => a.x - b.x);
    return { left: placed[0].hand, right: placed[placed.length - 1].hand };
  }
  if (placed.length === 1) {
    return placed[0].x < 0.5 ? { left: placed[0].hand, right: null } : { left: null, right: placed[0].hand };
  }
  return { left: null, right: null };
}
